import React, { useMemo, useState } from 'react';
import { HiOutlineEnvelope, HiOutlineEye, HiOutlineEyeSlash, HiOutlineLockClosed, HiOutlineMoon, HiOutlineSun, HiOutlineUserCircle } from 'react-icons/hi2';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import logo from './assets/logo.png';
import { useTheme } from './context/ThemeContext';
import { useUserAuth } from './context/UserAuthContext';

const Signup = () => {
  const navigate = useNavigate();
  const { signUp } = useUserAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const errors = useMemo(
    () => ({
      name: !data.name.trim() ? 'Name is required.' : '',
      email: !data.email.trim() ? 'Email address is required.' : '',
      password: data.password.length < 6 ? 'Password must be at least 6 characters.' : '',
      confirmPassword: data.confirmPassword !== data.password ? 'Passwords must match.' : '',
    }),
    [data],
  );

  const hasErrors = Boolean(errors.name || errors.email || errors.password || errors.confirmPassword);

  const formHandler = (event) => {
    const { name, value } = event.target;
    setData((currentData) => ({ ...currentData, [name]: value }));
  };

  const signupHandler = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (hasErrors) {
      toast.error('Please complete the highlighted fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      await signUp(data.email, data.password);
      toast.success('Account created successfully.');
      navigate('/dashboard/dash_board');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <button className="theme-fab" type="button" onClick={toggleTheme} aria-label="Toggle color theme">
        {isDarkMode ? <HiOutlineSun aria-hidden="true" /> : <HiOutlineMoon aria-hidden="true" />}
      </button>

      <section className="auth-card">
        <div className="auth-brand">
          <img src={logo} alt="Event Planet" className="auth-logo" />
          <span className="badge rounded-pill text-bg-success-subtle">New Workspace</span>
        </div>

        <div className="auth-copy">
          <h1>Create account</h1>
          <p>Set up access for the Event Planet publishing console.</p>
        </div>

        <form className="auth-form" onSubmit={signupHandler} noValidate>
          <div className="mb-3">
            <label className="form-label" htmlFor="signup-name">
              Full name
            </label>
            <div className="input-group input-group-modern">
              <span className="input-group-text">
                <HiOutlineUserCircle aria-hidden="true" />
              </span>
              <input
                type="text"
                className={`form-control form-control-lg ${submitted && errors.name ? 'is-invalid' : ''}`}
                id="signup-name"
                placeholder="Your name"
                name="name"
                onChange={formHandler}
                value={data.name}
                autoComplete="name"
              />
              {submitted && errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="signup-email">
              Email address
            </label>
            <div className="input-group input-group-modern">
              <span className="input-group-text">
                <HiOutlineEnvelope aria-hidden="true" />
              </span>
              <input
                type="email"
                className={`form-control form-control-lg ${submitted && errors.email ? 'is-invalid' : ''}`}
                id="signup-email"
                placeholder="you@example.com"
                name="email"
                onChange={formHandler}
                value={data.email}
                autoComplete="email"
              />
              {submitted && errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label" htmlFor="signup-password">
                Password
              </label>
              <div className="input-group input-group-modern">
                <span className="input-group-text">
                  <HiOutlineLockClosed aria-hidden="true" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control form-control-lg ${submitted && errors.password ? 'is-invalid' : ''}`}
                  id="signup-password"
                  placeholder="Create password"
                  name="password"
                  onChange={formHandler}
                  value={data.password}
                  autoComplete="new-password"
                />
                <button
                  className="btn btn-input-action"
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((currentValue) => !currentValue)}
                >
                  {showPassword ? <HiOutlineEyeSlash aria-hidden="true" /> : <HiOutlineEye aria-hidden="true" />}
                </button>
                {submitted && errors.password && <div className="invalid-feedback">{errors.password}</div>}
              </div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label" htmlFor="signup-confirm-password">
                Confirm password
              </label>
              <div className="input-group input-group-modern">
                <span className="input-group-text">
                  <HiOutlineLockClosed aria-hidden="true" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control form-control-lg ${submitted && errors.confirmPassword ? 'is-invalid' : ''}`}
                  id="signup-confirm-password"
                  placeholder="Repeat password"
                  name="confirmPassword"
                  onChange={formHandler}
                  value={data.confirmPassword}
                  autoComplete="new-password"
                />
                {submitted && errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
              </div>
            </div>
          </div>

          <button className="btn btn-primary btn-lg w-100 mt-4" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                Creating account
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>

        <p className="auth-footnote">
          Already have an account? <Link to="/">Sign in</Link>
        </p>
      </section>
    </main>
  );
};

export default Signup;
